<template>
  <label
    class="ak-checkbox"
    rel="root"
    :disabled="disabled"
    @click.stop="focus"
    @keydown.prevent.enter="root.click()"
  >
    <input
      ref="input"
      v-model="computedValue"
      type="checkbox"
      :indeterminate.prop="indeterminate"
      :disabled="disabled"
      :required="required"
      :name="name"
      :value="nativeValue"
      :true-value="trueValue"
      :false-value="falseValue"
      @click.stop
      @change="validate"
    />
    <span class="ak-checkbox__replacement"></span>
    <template v-if="label">
      <span class="ak-checkbox__body">
        <slot />
      </span>
    </template>
  </label>
</template>

<script>
import { defineComponent, ref, onMounted, onBeforeUnmount } from 'vue'

import {
  checkboxProps,
  checkRadioProps,
  formElementProps,
  validationProps,
} from '../../mixins/formPropsMixin'

import checkRadioMixin from '../../mixins/checkRadioMixin'
import validationMixin from '../../mixins/validationMixin'
import formElementMixin from '../../mixins/formElementMixin'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-checkbox value="one"></ak-checkbox>
   */
  name: 'AkCheckbox',

  inheritAttrs: false,

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    ...checkboxProps,
    ...checkRadioProps,
    ...formElementProps,
    ...validationProps,
  },

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @param {Function} options.attrs - The attributes for the component.
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit, attrs }) {
    const input = ref(null)
    const root = ref(null)

    /**
     * @type {Object}
     * @description The validation mixin return value, this is an object
     * containing all of the required functionality to handle the
     * validation of the input
     */
    const validation = validationMixin({
      type: 'checkbox',
      emit,
      props,
      attrs,
      input,
    })

    /**
     * @type {Object}
     * @description The form element mixin return value, this is an object
     * that contains all of the shared functionality for form elements
     */
    const formMixin = formElementMixin({
      emit,
      props,
      attrs,
      input,
      validation,
    })

    /**
     * @type {Object}
     * @description The checkRadioMixin return value, this is an object
     * that contains all of the shared functionality for the checkbox
     * and radio input types
     */
    const inputMixin = checkRadioMixin({
      type: 'checkbox',
      emit,
      props,
      validation,
    })

    onMounted(() => {
      inputMixin.onMounted()
      validation.validate()
    })

    onBeforeUnmount(() => {
      inputMixin.beforeUnmount()
      validation.beforeUnmount()
    })

    return {
      input,
      root,
      ...inputMixin,
      ...validation,
      ...formMixin,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
.ak-checkbox {
  $self: &;
  display: inline-flex;
  align-items: center;
  // TODO: Remove this font size when we remove angular styles - its overriding
  // the default label sizing
  font-size: inherit;

  input[type='checkbox'] {
    opacity: 0;
    position: absolute;
    width: 1.5rem;
    height: 1.5rem;
  }

  &__replacement {
    display: flex;
    align-items: center;
    justify-content: center;
    align-self: flex-start;
    flex-shrink: 0;
    position: relative;
    width: 1.5rem;
    height: 1.5rem;
    border: 1px solid $color-grey-light;
    border-radius: $base-button-radius;
    background-color: #fff;
    color: #fff;
    box-shadow: $form-box-shadow;
    transition: all $transition-time $ease-bounce;
    &::before {
      display: block;
      font-family: 'icons';
      speak: none;
      font-style: normal;
      font-weight: normal;
      font-variant: normal;
      text-transform: none;
      text-rendering: auto;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      vertical-align: middle;
      font-size: 1rem;
      content: '\e9c9';
      opacity: 1;
      transform: scale(0);
      transition: all 0.5s $ease-out-back;
    }
  }

  input:checked + #{$self}__replacement {
    color: #fff;
    border-color: $color-action;
    background-color: $color-action;

    &::before {
      opacity: 1;
      transform: scale(1);
    }
  }

  input:disabled + #{$self}__replacement {
    opacity: 0.75;
    background-color: $color-grey-lightest;
    border-color: $color-grey-light;
  }

  input:checked:disabled + #{$self}__replacement {
    opacity: 0.75;
    background-color: $color-grey-lightest;
    border-color: $color-grey-light;
  }

  &:hover #{$self}__replacement,
  input:focus + #{$self}__replacement {
    border-color: $color-action;
  }

  input:focus + #{$self}__replacement {
    box-shadow: $form-box-shadow-focus;
  }

  #{$self}__body {
    padding-left: $spacing-tiny;
    font-weight: normal;
  }
}
</style>
